import { Link } from "wouter";
import { useLocale } from "@/hooks/useLocale";
import type { Locale } from "@/lib/i18n/locales";

// Native name plus, for scripts a lot of visitors can't tell apart on
// sight (Japanese/Hindi/Chinese/Korean all look like "unfamiliar
// squiggles" to someone who doesn't read any of them), a plain-English
// clarifier in parentheses so the difference is obvious without needing
// to already know the script. Latin-alphabet languages don't get one --
// "Français" and "Deutsch" are already legible as "clearly not English"
// even to someone who doesn't speak French or German.
const TICKER_ITEMS: { locale: Locale; flag: string; name: string; clarifier?: string }[] = [
  { locale: "en", flag: "\u{1F1FA}\u{1F1F8}", name: "English" },
  { locale: "es", flag: "\u{1F1EA}\u{1F1F8}", name: "Espa\u00f1ol" },
  { locale: "pt", flag: "\u{1F1E7}\u{1F1F7}", name: "Portugu\u00eas" },
  { locale: "fr", flag: "\u{1F1EB}\u{1F1F7}", name: "Fran\u00e7ais" },
  { locale: "de", flag: "\u{1F1E9}\u{1F1EA}", name: "Deutsch" },
  { locale: "ja", flag: "\u{1F1EF}\u{1F1F5}", name: "\u65e5\u672c\u8a9e", clarifier: "Japanese" },
  { locale: "hi", flag: "\u{1F1EE}\u{1F1F3}", name: "\u0939\u093f\u0928\u094d\u0926\u0940", clarifier: "Hindi" },
  { locale: "zh", flag: "\u{1F1E8}\u{1F1F3}", name: "\u4e2d\u6587", clarifier: "Mandarin" },
  { locale: "ko", flag: "\u{1F1F0}\u{1F1F7}", name: "\ud55c\uad6d\uc5b4", clarifier: "Korean" },
  { locale: "id", flag: "\u{1F1EE}\u{1F1E9}", name: "Bahasa Indonesia" },
];

/**
 * A slim, continuously-scrolling strip of every supported language,
 * sitting between the hero and the Corporate-Safe section. Purely an
 * ambient "we support this many languages" signal -- the actual
 * language-switching control remains the header's LanguageSwitcher
 * dropdown. Each item still links to that locale's homepage, so it's
 * not purely decorative, but discovering the ticker isn't how anyone's
 * expected to actually switch languages day to day.
 *
 * Two accessibility details that matter more than the animation itself:
 * - Pauses on hover/focus, since a moving target is hard to read or
 *   click if it never stops.
 * - Falls back to a static, non-scrolling row for anyone with
 *   prefers-reduced-motion set at the OS level -- handled entirely in
 *   CSS (see index.css's .language-ticker-track rule), not duplicated
 *   here in JS.
 */
export function LanguageTicker() {
  const { pathInLocale } = useLocale();
  // Rendered twice back to back so the CSS animation can scroll exactly
  // one copy's width and loop seamlessly -- the visual seam lands
  // between an identical pair of sequences, so it's imperceptible.
  const items = [...TICKER_ITEMS, ...TICKER_ITEMS];

  return (
    <div className="language-ticker" aria-label="Available in 10 languages">
      <div className="language-ticker-track">
        {items.map((item, i) => (
          <Link
            key={`${item.locale}-${i}`}
            href={pathInLocale(item.locale)}
            className="language-ticker-item"
            aria-hidden={i >= TICKER_ITEMS.length ? "true" : undefined}
            tabIndex={i >= TICKER_ITEMS.length ? -1 : undefined}
          >
            <span aria-hidden="true">{item.flag}</span>
            <span>
              {item.name}
              {item.clarifier && <span className="language-ticker-clarifier"> ({item.clarifier})</span>}
            </span>
          </Link>
        ))}
      </div>
    </div>
  );
}
